import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import InnerImageZoom from '../InnerImageZoom/InnerImageZoom';
import './styles.css';
import './React360Viewer.css';
import Growm360Viewer from '../Growm360Viewer/Growm360Viewer';

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

const parseImageUrlComponents = (sampleUrl, baseUrl) => {
  if (!sampleUrl || !baseUrl) {
    return { filenamePrefix: '', numericPart: '', suffixPart: '', filetype: 'jpg', query: '' };
  }

  const relPath = sampleUrl.startsWith(baseUrl) ? sampleUrl.substring(baseUrl.length) : sampleUrl;
  const qIdx = relPath.indexOf('?');
  const file = qIdx === -1 ? relPath : relPath.substring(0, qIdx);
  const query = qIdx === -1 ? '' : relPath.substring(qIdx);

  const dotIdx = file.lastIndexOf('.');
  if (dotIdx === -1) {
    // No extension, treat as error or handle as per requirements
    return { filenamePrefix: file, numericPart: '', suffixPart: '', filetype: '', query };
  }
  const name = file.substring(0, dotIdx);
  const filetype = file.substring(dotIdx + 1);

  // Try to find the last numeric sequence in the name part
  const matchNumeric = name.match(/(\d+)(?!.*\d)/);
  const numericPart = matchNumeric ? matchNumeric[1] : '';
  const filenamePrefix = matchNumeric ? name.substring(0, matchNumeric.index) : name; // Part before the numeric index
  const suffixPart = matchNumeric ? name.substring(matchNumeric.index + numericPart.length) : ''; // Part after numeric index, before extension (e.g., _full)

  console.log('filenamePrefix:', filenamePrefix);
  console.log('numericPart:', numericPart);
  console.log('suffixPart:', suffixPart);
  console.log('filetype:', filetype);
  console.log('query:', query);

  return { filenamePrefix, numericPart, suffixPart, filetype, query };
};

// const InnerImageZoom = lazy(() => import('../InnerImageZoom/InnerImageZoom'));
// const Growm360Viewer = lazy(() => import('../Growm360Viewer/Growm360Viewer'));

const ProductViewerComponent = ({ autoplayViewer, imageToShow, forceUpdate, onClickZoom, productSettings, images360, images360HD,
  aspectRatio, parentWidth, parentHeight, showRotationIconOnStartup, imagesCount = 36, is180Pan = false }) => {

  const imagesBaseUrl = images360[imageToShow].split('/').slice(0, -1).join('/') + '/'; //`${cloudFrontUrl}/${productFolder}/`;
  const [isZoomed, setIsZoomed] = useState(false);
  const [toggleZoomTimer, setToggleZoomTimer] = useState(false); // Extra toggle variable to delay zoom toggling to avoid zoom getting stuck
  //Need to get images from assetsUrls if available
  const [currentImageUrl, setCurrentImageUrl] = useState(images360[imageToShow]);
  const [currentHDImageUrl, setCurrentHDImageUrl] = useState(images360HD[imageToShow]);
  const [lastImageIndex, setLastImageIndex] = useState(0);
  const lastImageIndexRef = useRef(lastImageIndex);
  const [lastOverrideIndex, setLastOverrideIndex] = useState(0);
  const lastOverrideIndexRef = useRef(lastOverrideIndex);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isReadyToAutoplay, setIsReadyToAutoplay] = useState(false);
  const [currentImageToShow, setCurrentImageToShow] = useState(imageToShow);
  const [imageElements, setImageElements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasUserClicked, setHasUserClicked] = useState(false);
  const [isMaxImageIndexReached, setIsMaxImageIndexReached] = useState(false);
  const isMaxImageIndexReachedRef = useRef(isMaxImageIndexReached);
  const [isMinImageIndexReached, setIsMinImageIndexReached] = useState(true);
  const isMinImageIndexReachedRef = useRef(isMinImageIndexReached);
  const isFirstRender = useRef(true);
  const [stdImageConfig, setStdImageConfig] = useState({ prefix: '', filetype: 'jpg', query: '', suffixPart: '' });
  const [hdImageConfig, setHdImageConfig] = useState({ prefix: '', filetype: 'jpg', query: '', suffixPart: '_full' });
  const stdImageConfigRef = useRef(stdImageConfig);
  const hdImageConfigRef = useRef(hdImageConfig);

  const toggleZoom = () => {
    // currentImageToShow is the 0-based index
    onClickZoom(currentImageToShow);
    handleZoomToggle();
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setTimeout(() => {
      onClickZoom(currentImageToShow);
      handleZoomToggle();
    }, 100);
  }, [toggleZoomTimer]);

  useEffect(() => {
    stdImageConfigRef.current = stdImageConfig;
    hdImageConfigRef.current = hdImageConfig;
  }, [stdImageConfig, hdImageConfig]);

  useEffect(() => {
    if (images360 && images360.length > 0 && imagesBaseUrl) {
      const parsedStd = parseImageUrlComponents(images360[0], imagesBaseUrl);
      setStdImageConfig(prev => ({
        ...prev,
        prefix: parsedStd.filenamePrefix,
        filetype: parsedStd.filetype,
        query: parsedStd.query,
        suffixPart: parsedStd.suffixPart
      }));
    } else {
      setStdImageConfig({ prefix: '', filetype: 'jpg', query: '', suffixPart: '' });
    }
  }, [images360, imagesBaseUrl]);

  useEffect(() => {
    if (images360HD && images360HD.length > 0 && imagesBaseUrl) {
      const parsedHd = parseImageUrlComponents(images360HD[0], imagesBaseUrl);
      let hdSuffix = '_full'; // Default
      if (parsedHd.suffixPart) {
        if (parsedHd.suffixPart.endsWith('_full')) {
          hdSuffix = parsedHd.suffixPart;
        } else {
          hdSuffix = parsedHd.suffixPart + '_full';
        }
      } else if (stdImageConfig.suffixPart && stdImageConfig.suffixPart !== '_full' && !stdImageConfig.suffixPart.endsWith('_full')) {
        // If std has a suffix but it's not already an HD one, append _full
        hdSuffix = stdImageConfig.suffixPart + '_full';
      } else if (stdImageConfig.suffixPart.endsWith('_full')) {
        // If std already has _full, use that
        hdSuffix = stdImageConfig.suffixPart;
      }
      // If none of the above, default _full is already set
      setHdImageConfig(prev => ({
        ...prev,
        prefix: parsedHd.filenamePrefix,
        filetype: parsedHd.filetype,
        query: parsedHd.query,
        suffixPart: hdSuffix
      }));
    } else {
      // Default HD config might assume a common pattern like '_full' if not derivable
      setHdImageConfig({ prefix: stdImageConfig.prefix, filetype: stdImageConfig.filetype, query: stdImageConfig.query, suffixPart: '_full' });
    }
  }, [images360HD, imagesBaseUrl, stdImageConfig]);

  // useEffect(() => {
  //   if (isZoomed) {
  //     // Optionally add a delay if needed:
  //     const timeoutId = setTimeout(() => {
  //       const imageElement = document.querySelector('.innerImageZoom img'); 
  //       if (imageElement) {
  //         const touchStartEvent = new TouchEvent('touchstart', {
  //           bubbles: true,
  //           cancelable: true,
  //           touches: [new Touch({
  //             identifier: Date.now(),
  //             target: imageElement,
  //             clientX: imageElement.getBoundingClientRect().left,
  //             clientY: imageElement.getBoundingClientRect().top
  //           })]
  //         });

  //         const touchEndEvent = new TouchEvent('touchend', {
  //           bubbles: true,
  //           cancelable: true,
  //           touches: [],
  //           changedTouches: [new Touch({
  //             identifier: Date.now(),
  //             target: imageElement,
  //             clientX: imageElement.getBoundingClientRect().left,
  //             clientY: imageElement.getBoundingClientRect().top
  //           })]
  //         });

  //         imageElement.dispatchEvent(touchStartEvent);
  //         imageElement.dispatchEvent(touchEndEvent);
  //       }
  //     }, 50); // Adjust delay as needed

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [isZoomed]);

  const handleZoomToggle = () => {
    setIsZoomed(!isZoomed);
  };

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  const ZoomButton = ({ onClickZoom, handleZoomToggle, currentImageToShow }) => {

    const handleClick = () => {
      onClickZoom(currentImageToShow);
      handleZoomToggle();
    };

    return (
      <button className="buttonZoom" onClick={handleClick} />
    );
  };

  const ExtractImageIndexFromUrl = (urlString) => {
    if (!urlString || !imagesBaseUrl || !stdImageConfig.filetype) return null;

    let relativePath = urlString;
    if (relativePath.startsWith(imagesBaseUrl)) {
      relativePath = relativePath.substring(imagesBaseUrl.length);
    }

    //relativePath is e.g. productName_1.jpg?295
    // Remove query string if present
    const queryIndex = relativePath.indexOf('?');
    if (queryIndex !== -1) {
      relativePath = relativePath.substring(0, queryIndex);
    }

    // Expected format: prefix + numericIndex + suffixPart + .filetype
    const expectedSuffix = (stdImageConfig.suffixPart || '') + '.' + stdImageConfig.filetype;

    //relativePath is now e.g. productName_1.jpg. Where is prefix set and is it set correctly? If it's not set, it will fail here
    if (!relativePath.startsWith(stdImageConfig.prefix) || !relativePath.endsWith(expectedSuffix)) {
      // Fallback for simple numeric match if complex pattern fails
      const simpleMatch = relativePath.match(/(\d+)(?!.*\d)/);
      return simpleMatch ? simpleMatch[1] : null;
    }

    const potentialNumericPart = relativePath.substring(
      stdImageConfig.prefix.length,
      relativePath.length - expectedSuffix.length
    );


    if (/^\d+$/.test(potentialNumericPart)) {
      return potentialNumericPart;
    }
    // Fallback for simple numeric match if specific parsing fails
    const fallbackMatch = relativePath.match(/(\d+)(?!.*\d)/);
    console.log('fallbackMatch:', fallbackMatch);
    return fallbackMatch ? fallbackMatch[1] : null;
  };


  useEffect(() => {
    let visibleImageObserver = null;

    const checkVisibleImage = () => {
      const visibleImage = document.querySelector(
        'img[alt="Rotating object"][style*="display: block"]'
      );

      if (visibleImage) {
        const src = visibleImage.getAttribute('src');
        const imageIndex = ExtractImageIndexFromUrl(src);

        // console.log('current image index:', imageIndex, 'image to show:', imageToShow);
        // console.log('set viewer image to:', imageToShow !== currentImageToShow ? imageToShow : currentImageToShow);
        const arrayIndex = imageIndex - 1; // imageIndex is 1-based from Growm360Viewer
        setCurrentImageToShow(arrayIndex);

        // Use the image index to directly access the corresponding image from the arrays
        // This allows each image to have its own unique baseUrl
        const foundStdImage = images360 && images360[arrayIndex] ? images360[arrayIndex] : src;
        const foundHdImage = images360HD && images360HD[arrayIndex] ? images360HD[arrayIndex] : foundStdImage;

        // console.log(`Zoom HD Image Debug: imageIndex=${imageIndex}, arrayIndex=${arrayIndex}`);
        // console.log(`  Standard [${arrayIndex}]: ${foundStdImage}`);
        // console.log(`  HD [${arrayIndex}]: ${foundHdImage}`);
        // console.log(`  images360HD array length: ${images360HD?.length || 0}`);

        if (foundHdImage && foundHdImage !== currentHDImageUrl) {
          // Update current images using the direct array access
          console.log(`  Setting currentHDImageUrl to: ${foundHdImage}`);
          setCurrentImageUrl(foundStdImage);
          setCurrentHDImageUrl(foundHdImage);
        }
      }
    };

    const observeVisibleImage = () => {
      // Continuously check for visible image
      const intervalId = setInterval(checkVisibleImage, 100);

      // Cleanup on unmount
      return () => {
        clearInterval(intervalId);
        if (visibleImageObserver) {
          visibleImageObserver.disconnect();
        }
      };
    };

    const observe360Container = () => {
      if (!stdImageConfig.filetype || !hdImageConfig.filetype) return; // Don't run if configs not ready
      // Watch the container for DOM changes
      const parentElement = document.querySelector('.viewer360-container'); // Adjust selector as needed

      if (parentElement) {
        const mutationObserver = new MutationObserver(() => {
          // Whenever DOM changes occur, check the visible image
          checkVisibleImage();
        });

        mutationObserver.observe(parentElement, {
          childList: true,
          subtree: true,
        });

        // Cleanup observer on unmount
        return () => {
          mutationObserver.disconnect();
        };
      }
    };

    // Use both interval and MutationObserver for robustness
    observe360Container();
    return observeVisibleImage();
  }, [images360HD, currentHDImageUrl]);

  useEffect(() => {
    if (!hasUserClicked) return;
    // Get all images within the viewer container
    const container = document.querySelector('.react-360-viewer-container');
    if (container) {
      const images = container.querySelectorAll('img[alt="Rotating object"]');
      const imagesArray = Array.from(images);

      setImageElements(imagesArray);

      // Find the initially visible image
      //const initialIndex = imagesArray.findIndex(img => img.style.display === 'block');
      //if (initialIndex !== -1) setCurrentIndex(initialIndex);
    }
  }, [hasUserClicked]);

  useEffect(() => {
    lastImageIndexRef.current = lastImageIndex; // Keep ref updated with latest value
    lastOverrideIndexRef.current = lastOverrideIndex;
  }, [lastImageIndex, lastOverrideIndex]);

  // Sync state when parent updates imageToShow
  useEffect(() => {
    setCurrentImageToShow(imageToShow);
  }, [imageToShow]);

  useEffect(() => {
    window.addEventListener(`resize`, handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = [];
      if (!images360 || images360.length === 0) {
        console.warn('Cannot preload images: images360 array not available.');
        setIsReadyToAutoplay(true); // Allow proceeding without preloading if images are missing
        return;
      }
      for (let i = 0; i < imagesCount; i++) {
        const img = new Image();
        // Use the full URL from the images360 array directly, allowing each image to have its own unique baseUrl
        img.src = images360[i] || `${imagesBaseUrl}${stdImageConfig.prefix}${i + 1}${stdImageConfig.suffixPart}.${stdImageConfig.filetype}${stdImageConfig.query}`;
        imagePromises.push(new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // In case some images fail to load, continue
        }));
      }
      await Promise.all(imagePromises);
      //Wait 1 sec to avoid blinking images during 360 spin
      setTimeout(() => {
        setIsReadyToAutoplay(true);
      }, 1000);
    };

    preloadImages();
  }, [images360, imagesCount, imagesBaseUrl, stdImageConfig]);

  //Array of images
  //const myImages = images; //Array.from({ length: 36 }, (_, i) => `https://exs-360-images.s3.eu-west-2.amazonaws.com/Armchair_teagan/${i + 1}.jpg`);

  const styles = {
    maxWidth: '900px',
    width: '800px'
  };

  //console.log('image to show, current:', imageToShow, currentImageToShow);
  return (
    <div
      className="container360"
      style={{
        // width: Math.min(aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000),
        // height: Math.min(1 / aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000),
        // minWidth: Math.min(aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000), //Ensure min width during re-render to avoid empty space
        // minHeight: Math.min(1 / aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000),
        position: 'relative'
      }}
    >
      {/* <div className={`zoom-button-container ${isZoomed ? `expanded` : ''}`}>
        <ZoomButton
          onClickZoom={onClickZoom}
          handleZoomToggle={handleZoomToggle}
          currentImageUrl={currentImageUrl}
        />      
      </div> */}
      {!isZoomed ? (
        <div
          className='react-360-viewer-container'
          style={{
            // width: Math.min(aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000),
            // height: Math.min(1 / aspectRatio, 1) * 0.9 * Math.min(windowWidth, 1000),
            Width: parentWidth, //Ensure min width during re-render to avoid empty space
            Height: parentHeight,
            transition: 'opacity 0.3s ease-in-out',
          }}
        //Double click disabled due to an issue with inner image zoom keeping ownership of the mouse and preventing
        //360 viewer to behave normally
        //onClick={handleDoubleClick}
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Growm360Viewer
              className="react-360-viewer"
              //Force to re-render when we click on the same pointOfView button as the last one pressed if the user has rotated the object
              imagesCount={imagesCount}
              imagesUrls={images360} // Pass full URLs array so each image can have its own baseUrl
              imagesBaseUrl={imagesBaseUrl}
              imagesFiletype={stdImageConfig.filetype || 'jpg'}
              imageFilenamePrefix={stdImageConfig.prefix || ''}
              imageQueryString={stdImageConfig.query || ''} // Keep for backward compatibility
              autoplay={autoplayViewer && isReadyToAutoplay}
              autoplaySpeed={productSettings.growm360autoplaySpeed}
              imageInitialIndex={imageToShow}
              imageInitialIndexTrigger={forceUpdate}
              is180Pan={is180Pan}
              autoplayTarget={is180Pan ? Math.round(imagesCount / 2) : 0} // Stop half-way through the pan for a 180 pan
              mouseDragSpeed={1}
              onPointerClick={toggleZoom}
              showRotationIconOnStartup={showRotationIconOnStartup}
              width={parentWidth}
              height={parentHeight}
              style={{
                Width: parentWidth, //Ensure min width during re-render to avoid empty space
                Height: parentHeight,
              }}
            />
          </Suspense>
        </div>) : (
        <div
          className={`inner-image-zoom-container ${isZoomed ? `expanded` : ''}`}
          style={{
            transition: 'opacity 0.3s ease-in-out',
          }}
          onClick={() => { console.log('image clicked'); }}
        //onClick={toggleZoom}
        //onTouchEnd={toggleZoom}
        >
          <Suspense fallback={<div>Loading...</div>}>
            <InnerImageZoom
              className="innerImageZoom"
              src={currentImageUrl}
              zoomSrc={currentHDImageUrl}
              zoomType="hover"
              fullscreenOnMobile={false}
              hideCloseButton={true}
              moveType='drag'
              hideHint={true}
              afterZoomOut={toggleZoom}
              alt='zoom360'
              width={parentWidth}
              height={parentHeight}
            //style={{ width: '640px', height: 'auto', cursor: 'pointer', padding: 5 }}
            />
          </Suspense>
        </div>
      )
      }
    </div>
  );
};

export default ProductViewerComponent;
